import styled from 'styled-components';
import { Form, Formik, type FormikHelpers, type FormikValues } from 'formik';
import HTTPMethod from 'http-method-enum';

import { media, pxToRem } from '@yoweb/ui/styles/utils';
import { Button } from '@yoweb/ui/components/buttons';
import { Title } from '@yoweb/ui/components/typography';
import { th } from '@yoweb/ui/styles/utils/theme';
import { Box } from '@yoweb/ui/components/Box';
import NameGrid from '@yoweb/ui/components/NameGrid';
import { Spacer } from '@yoweb/ui/components/Spacer';
import { useError, ErrorDisplay, Select, Input, Textarea } from '@yoweb/ui/components/form';
import { useTranslation } from '@yoweb/next-i18next';
import { useYoForm } from '@yoweb/ui/hooks/forms';
import form, { getTopicsOptions } from '@yoweb/ui/components/help/form';
import { fetchJson } from '@yoweb/utils/fetchJson';
import useReCaptcha from '@yoweb/ui/hooks/useReCaptcha';

const IDS = {
  faqMenu: 'faq-section-menu',
  faqSectionDesktop: 'faq-section-desktop',
  faqSectionDesktopQH: 'faq-section-desktop-qh',
  faqSectionDesktopTitle: 'faq-section-desktop-title',
  faqSectionMobile: 'faq-section-mobile',
  faqSectionMobileTitle: 'faq-section-mobile-title',
  faqSectionMobileQH: 'faq-section-mobile-qh',
  submit: 'faq-question-submit',
  form: 'faq-question-form',
  error: 'faq-question-error',
};

export const API_PATH = '/api';
export const API_FAQ_QUESTION_FORM_SUBMISSION = `${API_PATH}/faq/form-submit`;
export const ACTION_FAQ_QUESTION_FORM_SUBMISSION = 'faq/form';

type FaqCTASectionProps = {
  onSubmitted: () => void;
  isReCaptchaActive?: boolean;
} & DataTestId;

const fieldSetRowGapWidth = pxToRem(18);

export const FaqCTAForm = ({ isReCaptchaActive = false, onSubmitted }: FaqCTASectionProps) => {
  const { t, i18n } = useTranslation(['common', 'help-form']);
  const errorDisplay = useError();
  const showError = Boolean(errorDisplay.message);
  const options = getTopicsOptions(t);
  const reCaptcha = useReCaptcha();
  const yoForm = useYoForm('common', form);

  const handleSubmit = async (values: FormikValues, helpers: FormikHelpers<FormikValues>) => {
    errorDisplay.clear();
    try {
      const requestBody: Record<string, unknown> = { ...values, locale: i18n.language };
      if (isReCaptchaActive) {
        requestBody.reCaptchaToken = await reCaptcha.execute(ACTION_FAQ_QUESTION_FORM_SUBMISSION);
      }

      await fetchJson(API_FAQ_QUESTION_FORM_SUBMISSION, {
        method: HTTPMethod.POST,
        body: JSON.stringify(requestBody),
      });
      helpers.resetForm();
      onSubmitted();
    } catch (error) {
      errorDisplay.set({
        title: t('error.title'),
        message: t('error.description'),
      });

      throw error;
    } finally {
      helpers.setSubmitting(false);
    }
  };

  return (
    <CTAFormSection>
      <FormContainer>
        {showError && (
          <Box mb="normal2">
            <ErrorDisplay withBorder {...errorDisplay} data-testid={IDS.error} />
          </Box>
        )}

        <Formik
          initialValues={yoForm.initialState}
          validationSchema={yoForm.schema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, isValid, dirty }) => (
            <Form data-testid={IDS.form}>
              <Fieldset role="group" aria-labelledby="topic-title">
                <Title weight="bold" size={{ _: 'xs', md: 'sm' }} id="topic-title">
                  {t('help-form:topic.title')}
                </Title>
                <Select
                  id="topic"
                  name="topic"
                  label={t('help-form:topic.label')}
                  options={options}
                  data-testid="faq-topic-select"
                />
                <NameGrid needsPronunciationFields={false} rowGapWidth={fieldSetRowGapWidth}>
                  <Input id="givenName" name="givenName" label={t('help-form:first-name.label')} />
                  <Input id="familyName" name="familyName" label={t('help-form:last-name.label')} />
                </NameGrid>
                <Input id="email" name="email" type="email" label={t('help-form:email.label')} />
                <Textarea
                  id="description"
                  name="description"
                  label={t('help-form:information.how-can-we-help-placeholder')}
                />
              </Fieldset>
              <Spacer size="normal3" />
              <FullWidth>
                <Button
                  size="lg"
                  type="submit"
                  data-testid={IDS.submit}
                  isLoading={isSubmitting}
                  isDisabled={!(dirty && isValid)}
                >
                  {t('actions.submit')}
                </Button>
              </FullWidth>
            </Form>
          )}
        </Formik>
      </FormContainer>
    </CTAFormSection>
  );
};

const FullWidth = styled.div`
  display: grid;
`;

const CTAFormSection = styled.div`
  position: relative;
  display: grid;
  grid-column: 1/5;
  ${media.md`
    grid-column: 5/9;
  `}
  ${media.lg`
    grid-column: 7/12;
  `}
`;

const FormContainer = styled.div`
  background-color: ${th.getColor('base000')};
  padding: ${th.getSpace('normal4')} ${th.getSpace('normal2')} ${th.getSpace('normal4')};
  ${media.md`
    border-radius: ${th.getRadii('medium')};  
    margin-top: unset;
    padding: ${th.getSpace('medium1')} ${th.getSpace('normal4')} ${th.getSpace('normal4')};
  `}
`;

const Fieldset = styled.div`
  display: grid;
  row-gap: ${fieldSetRowGapWidth};
`;
